import { apiFetch, getIdToken } from './auth-service';

export interface NotificationPrefs {
  emailNotifications: boolean;
  pushNotifications: boolean;
  weeklyDigest: boolean;
  mentorUpdates: boolean;
  courseUpdates: boolean;
}

export interface PrivacyPrefs {
  profileVisibility: boolean;
  activityStatus: boolean;
  dataAnalytics: boolean;
}

export interface UserProfile {
  _id: string;
  firebaseUid: string;
  email: string;
  emailVerified: boolean;
  name: string;
  avatar: string;
  role: 'founder' | 'mentor' | 'admin' | 'investor' | 'partner' | 'associate';
  userType?: 'founder' | 'mentor' | 'admin' | 'investor' | 'partner' | 'associate';
  status?: 'active' | 'pending' | 'approved' | 'rejected' | 'suspended';
  mentorStatus?: 'pending' | 'approved' | 'rejected';
  startups: string[];
  mentees: string[];
  permissions: string[];
  bio: string;
  company: string;
  title: string;
  phone: string;
  linkedIn: string;
  twitter: string;
  website: string;
  expertise: string[];
  experience: string;
  specialties: string[];
  region: string;
  industry: string[];
  preferences?: {
    notifications: NotificationPrefs;
    privacy: PrivacyPrefs;
  };
  createdAt: string;
  updatedAt: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/v1';

function flattenSocial(u: any): UserProfile {
  return {
    ...u,
    role: u.role || u.userType,
    startups: u.startups || [],
    mentees: u.mentees || [],
    permissions: u.permissions || (u.admin && u.admin.permissions) || [],
    bio: u.bio || '',
    company: u.company || '',
    title: u.title || '',
    phone: u.phone || '',
    linkedIn: (u.social && u.social.linkedIn) || '',
    twitter:  (u.social && u.social.twitter)  || '',
    website:  (u.social && u.social.website)  || '',
    expertise:   u.expertise   || (u.mentor && u.mentor.expertise)   || (u.investor && u.investor.expertise)   || [],
    specialties: u.specialties || (u.mentor && u.mentor.specialties) || (u.investor && u.investor.specialties) || [],
    experience:  u.experience  || (u.mentor && u.mentor.experience)  || '',
    region: u.region || '',
    industry: u.industry || [],
  } as UserProfile;
}

export const createUser = async (data: { name: string; role: string }): Promise<UserProfile> => {
  // Deprecated: registration goes through auth-service.signupWithEmail / signupWithGoogle.
  // Kept as a shim that returns the current /me so legacy callers don't throw.
  return getMe();
};

export const getMe = async (): Promise<UserProfile> => {
  const r = await apiFetch.get<{ user: any }>('/users/me');
  return flattenSocial(r.user);
};

export interface UpdateMeInput {
  // Top-level common fields the backend whitelists.
  name?: string; avatar?: string; phone?: string;
  bio?: string; company?: string; title?: string;
  region?: string; industry?: string[];
  // Social handles — sent nested under `social`.
  linkedIn?: string; twitter?: string; website?: string;
  // Role-specific subdocs — backend reads these per `userType`.
  founder?:   { stage?: string; startupName?: string; isFocalTrainee?: boolean };
  mentor?:    { expertise?: string[]; specialties?: string[]; experience?: string };
  partner?:   { institution?: string; titleRole?: string; studentCount?: string };
  associate?: { organization?: string; titleRole?: string; purpose?: string };
  investor?:  { fundName?: string; titleRole?: string; checkSize?: string; expertise?: string[]; specialties?: string[] };
}

export const updateMe = async (data: UpdateMeInput): Promise<UserProfile> => {
  const body: any = {};

  // Top-level whitelisted fields
  for (const k of ['name', 'avatar', 'phone', 'bio', 'company', 'title', 'region', 'industry'] as const) {
    if (data[k] !== undefined) body[k] = data[k];
  }

  // Social handles → nested under `social`
  const social: any = {};
  if (data.linkedIn !== undefined) social.linkedIn = data.linkedIn;
  if (data.twitter  !== undefined) social.twitter  = data.twitter;
  if (data.website  !== undefined) social.website  = data.website;
  if (Object.keys(social).length) body.social = social;

  // Role-specific subdocs — pass through as-is
  for (const role of ['founder', 'mentor', 'partner', 'associate', 'investor'] as const) {
    if (data[role]) body[role] = data[role];
  }

  const r = await fetchJson('/users/me', { method: 'PATCH', body: JSON.stringify(body) });
  return flattenSocial(r.user);
};

export const getUserById = async (id: string): Promise<{
  _id: string; name: string; avatar: string; bio: string; company: string;
  title: string; role: string; expertise: string[]; experience: string;
  specialties: string[]; region: string; industry: string[];
}> => {
  const r = await apiFetch.get<{ user: any }>(`/users/${id}`);
  const u = flattenSocial(r.user);
  return {
    _id: u._id, name: u.name, avatar: u.avatar, bio: u.bio, company: u.company,
    title: u.title, role: u.role, expertise: u.expertise, experience: u.experience,
    specialties: u.specialties, region: u.region, industry: u.industry,
  };
};

export const updatePreferences = async (data: {
  notifications?: Partial<NotificationPrefs>;
  privacy?: Partial<PrivacyPrefs>;
}): Promise<{ notifications: NotificationPrefs; privacy: PrivacyPrefs }> => {
  const r = await fetchJson('/users/me/preferences', { method: 'PATCH', body: JSON.stringify(data) });
  return r.preferences;
};

export const deleteAccount = async (): Promise<void> => {
  await fetchJson('/users/me', { method: 'DELETE' });
};

/**
 * Read a File into an HTMLImageElement so we can resize it on a canvas before
 * uploading. Resolves with the loaded image; rejects on any decode error.
 */
function fileToImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload  = () => resolve(img);
      img.onerror = () => reject(new Error('Could not decode image — try a different file.'));
      img.src = String(reader.result);
    };
    reader.onerror = () => reject(new Error('Could not read file.'));
    reader.readAsDataURL(file);
  });
}

/**
 * Resize the image to fit a max edge while preserving aspect ratio, then
 * encode as JPEG. Returns a base64 data URL safe to send over JSON.
 */
function resizeToDataUrl(img: HTMLImageElement, maxEdge = 256, quality = 0.85): string {
  const ratio = Math.min(1, maxEdge / Math.max(img.width, img.height));
  const w = Math.max(1, Math.round(img.width  * ratio));
  const h = Math.max(1, Math.round(img.height * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported in this browser.');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

const ACCEPTED_AVATAR_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const RAW_FILE_LIMIT_BYTES = 5 * 1024 * 1024; // 5 MB raw upload — we'll resize down

/**
 * Resize the avatar locally and persist it as a base64 data URL on the user
 * document. Returns the data URL (also reflected on session via applyToSession-like flow).
 */
export const uploadAvatar = async (file: File, _userId: string): Promise<string> => {
  if (!ACCEPTED_AVATAR_TYPES.includes(file.type)) {
    throw new Error('Avatar must be a JPG, PNG, or WEBP image.');
  }
  if (file.size > RAW_FILE_LIMIT_BYTES) {
    throw new Error('Image is larger than 5 MB — pick a smaller file.');
  }
  const img = await fileToImage(file);
  const dataUrl = resizeToDataUrl(img, 256, 0.85);

  const r = await fetchJson('/users/me/avatar', {
    method: 'POST',
    body: JSON.stringify({ dataUrl }),
  });
  return (r.user && r.user.avatar) || dataUrl;
};

export const getUsers = async (role?: string, status?: string): Promise<UserProfile[]> => {
  const params = new URLSearchParams();
  if (role)   params.set('userType', role);
  if (status) params.set('status', status);
  const path = `/users${params.toString() ? `?${params.toString()}` : ''}`;
  const r = await apiFetch.get<{ users: any[] }>(path);
  return r.users.map(flattenSocial);
};

export const adminDeleteUser = async (userId: string): Promise<void> => {
  await apiFetch.delete(`/users/admin/${userId}`);
};

export const approveMentor = async (userId: string): Promise<UserProfile> => {
  const r = await apiFetch.post<{ user: any }>(`/users/admin/${userId}/approve`);
  return flattenSocial(r.user);
};

export const rejectMentor = async (userId: string): Promise<UserProfile> => {
  const r = await apiFetch.post<{ user: any }>(`/users/admin/${userId}/reject`);
  return flattenSocial(r.user);
};

// ─── Generic JSON helper (we don't expose this from auth-service for non-POST verbs) ──
async function fetchJson(path: string, init: RequestInit = {}): Promise<any> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(init.headers as any) };
  const tok = await getIdToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  const res = await fetch(`${API_URL}${path}`, { ...init, headers });
  const text = await res.text();
  let body: any = null;
  try { body = text ? JSON.parse(text) : null; } catch { /* ignore */ }
  if (!res.ok) {
    const err: any = new Error(body?.error?.message || res.statusText || 'Request failed');
    err.status = res.status;
    err.code = body?.error?.code;
    throw err;
  }
  return body;
}
